import { DoubleRightOutlined, ClearOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { Flex, Spin, Input, Typography, Table, Layout, Button, Tooltip, App, Modal, Tag, Empty } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import useAuthStore from '@/stores/AuthStore'
import { useCustomerProfileStore } from '@/stores/customer-profile'
import { isEmpty } from '@haina/utils-commons'
import { Conditional } from '@/components/Conditional'
import { LineItem } from './components/line-item'

const CONTACT_COLUMNS = [
  {
    title: '邮箱',
    dataIndex: 'contact_value',
    render: (value) => value || '-',
  },
]

/**
 * Split Customer Profile Interface
 * Left: Current customer detail + email contact list (multi-select)
 * Right: New customer name input + preview
 */
function SplitCustomerProfile() {
  const [spinning, setSpinning] = useState(false)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [newCustomerName, setNewCustomerName] = useState('')
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const { notification } = App.useApp()

  const loginUser = useAuthStore((s) => s.loginUser)
  const {
    customerDetail,
    contactList: allContacts,
    splitCustomerProfile,
  } = useCustomerProfileStore(useShallow((s) => ({ customerDetail: s.customerDetail, contactList: s.contactList, splitCustomerProfile: s.splitCustomerProfile })))

  const contactList = useMemo(() => allContacts.filter((c) => c.contact_type === 'email'), [allContacts])

  const selectedContacts = useMemo(() => contactList.filter((c) => selectedRowKeys.includes(c.id)), [contactList, selectedRowKeys])

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([])
    setNewCustomerName('')
    setConfirmText('')
  }, [])

  const executeSplit = useCallback(async () => {
    if (selectedContacts.length === 0) {
      notification.warning({ message: '请先选择联系方式', placement: 'topLeft' })
      return
    }
    if (isEmpty(newCustomerName)) {
      notification.warning({ message: '请输入拆分出来的客人名字', placement: 'topLeft' })
      return
    }
    if (confirmText !== newCustomerName) {
      notification.warning({ message: '请输入正确的新客人名字以确认拆分', placement: 'topLeft' })
      return
    }
    if (!customerDetail?.global_customer_id) {
      notification.error({ message: '未找到当前客人信息', placement: 'topLeft' })
      return
    }

    setSpinning(true)
    splitCustomerProfile(newCustomerName, selectedRowKeys, `${loginUser.username}(${loginUser.userId})`)
      .then(() => {
        notification.success({
          message: '拆分成功',
          description: `已将 ${selectedContacts.length} 个联系方式拆分到新客人 "${newCustomerName}"`,
        })
      })
      .catch((err) => {
        notification.error({
          message: '出错了',
          description: err,
        })
      })
      .finally(() => {
        setSpinning(false)
        setConfirmModalOpen(false)
        setSelectedRowKeys([])
        setNewCustomerName('')
        setConfirmText('')
      })
  }, [selectedContacts, newCustomerName, confirmText, customerDetail, notification])

  const getCheckboxProps = useCallback(
    (record) => {
      if (contactList.length <= 1) {
        return { disabled: true }
      }
      const unselectedCount = contactList.length - selectedRowKeys.length
      const isChecked = selectedRowKeys.includes(record.id)
      if (!isChecked && unselectedCount <= 1) {
        return { disabled: true }
      }
      return {}
    },
    [contactList.length, selectedRowKeys],
  )

  const customerName = useMemo(() => (customerDetail?.first_name ?? '') + ' ' + (customerDetail?.last_name ?? ''), [customerDetail?.first_name, customerDetail?.last_name])

  return (
    <>
      <Spin spinning={spinning} fullscreen />

      <Modal
        title={
          <>
            <ExclamationCircleOutlined className='text-orange-500 mr-2' />
            确认拆分
          </>
        }
        open={confirmModalOpen}
        onOk={executeSplit}
        onCancel={() => {
          setConfirmModalOpen(false)
          setConfirmText('')
        }}
        okText='确认'
        cancelText='取消'
        okButtonProps={{
          danger: true,
          disabled: confirmText !== newCustomerName || isEmpty(newCustomerName),
        }}>
        <div className='py-4'>
          <Typography.Paragraph>
            <Typography.Text strong>
              你将拆分
              <span className='text-blue-600'> {selectedContacts.length} 个</span>
              邮箱到 <Tag color='green'>{newCustomerName}</Tag>
            </Typography.Text>
          </Typography.Paragraph>
          <div className='bg-gray-50 p-4 rounded-lg my-3'>
            <div className='flex flex-wrap gap-1'>
              {selectedContacts.map((c) => (
                <Tag key={c.id} color='blue'>
                  {c.contact_value}
                </Tag>
              ))}
            </div>
          </div>
          <div className='mt-4 p-3 bg-red-50 border border-red-200 rounded-lg'>
            <Typography.Text strong className='text-red-600'>
              拆分后不可撤销，请谨慎操作。
            </Typography.Text>
            <Input className='mt-2' placeholder={`请输入 "${newCustomerName}"`} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          </div>
        </div>
      </Modal>

      <Layout>
        <Layout.Sider width='420' theme='light' className='relative border-r border-gray-200'>
          <Typography.Title level={4}>
            <Tag color='blue' className='mr-2'>
              当前客人
            </Tag>
            档案
          </Typography.Title>

          <div className='bg-white p-1 max-w-4xl mx-auto pb-4 border-solid border-0 border-b border-gray-200 mb-3'>
            <LineItem title='名字' value={customerName || '-'} />
            <LineItem title='性别' value={customerDetail?.gender} />
            <LineItem title='国籍' value={customerDetail?.country} />
            <LineItem title='ID' value={customerDetail?.global_customer_id} />
          </div>

          <Typography.Title level={5}>
            联系方式
            {selectedContacts.length > 0 && <span className='text-sm font-normal text-blue-600 ml-1'>(已选 {selectedContacts.length})</span>}
          </Typography.Title>

          <Table
            rowKey='id'
            rowSelection={{
              hideSelectAll: true,
              selectedRowKeys,
              onChange: (newSelectedRowKeys) => {
                setSelectedRowKeys(newSelectedRowKeys)
              },
              getCheckboxProps,
            }}
            columns={CONTACT_COLUMNS}
            dataSource={contactList}
            pagination={false}
          />
        </Layout.Sider>

        <Layout.Content style={{ minWidth: '80px', maxWidth: '80px' }} className='flex items-center justify-center bg-gray-50'>
          <Flex vertical gap='middle' align='center'>
            <Tooltip title='执行拆分'>
              <Button
                type='primary'
                shape='circle'
                icon={<DoubleRightOutlined />}
                size='large'
                danger
                onClick={() => {
                  if (selectedContacts.length === 0) {
                    notification.warning({ message: '请先选择联系方式', placement: 'topLeft' })
                    return
                  }
                  if (isEmpty(newCustomerName)) {
                    notification.warning({ message: '请输入拆分出来的客人名字', placement: 'topLeft' })
                    return
                  }
                  setConfirmModalOpen(true)
                }}
                disabled={selectedContacts.length === 0 || isEmpty(newCustomerName)}
              />
            </Tooltip>
          </Flex>
        </Layout.Content>

        <Layout.Sider width='420' theme='light' className='relative border-l border-gray-200'>
          <Typography.Title level={4} className='mt-4'>
            <Tag color='green' className='mr-2'>
              拆分到
            </Tag>
            新档案
          </Typography.Title>

          <div className='p-4'>
            <Typography.Paragraph type='secondary'>在左侧勾选要拆分的邮箱，输入新客人名字后点击拆分按钮。</Typography.Paragraph>

            <div className='mt-4'>
              <Typography.Text strong>客人名字</Typography.Text>
              <Input
                className='mt-2'
                size='large'
                placeholder='请输入拆分出来的客人名字'
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                onPressEnter={() => {
                  if (selectedContacts.length > 0 && !isEmpty(newCustomerName)) {
                    setConfirmModalOpen(true)
                  }
                }}
                allowClear
              />
            </div>

            <Conditional
              condition={selectedContacts.length > 0}
              whenTrue={
                <div className='mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                  <Flex justify='space-between' align='center' className='mb-2'>
                    <Typography.Text strong className='text-blue-700'>
                      已选 {selectedContacts.length} 个邮箱
                    </Typography.Text>
                    <Button type='text' size='small' icon={<ClearOutlined />} onClick={clearSelection} danger>
                      清除全部
                    </Button>
                  </Flex>
                  <div className='flex flex-wrap gap-1'>
                    {selectedContacts.map((c) => (
                      <Tag
                        key={c.id}
                        closable
                        color='blue'
                        onClose={(e) => {
                          e.preventDefault()
                          setSelectedRowKeys((prev) => prev.filter((id) => id !== c.id))
                        }}>
                        {c.contact_value}
                      </Tag>
                    ))}
                  </div>
                </div>
              }
            />
          </div>
        </Layout.Sider>
      </Layout>
    </>
  )
}

export default SplitCustomerProfile
